import * as THREE from 'three';
import { global } from '../../core/GlobalState';
import { playerManager } from '../../core/player/PlayerManager';
import { ORB_FLASH_FEEDBACK_DURATION_MS, ORB_FLASH_OUTLINE_COLOR, ORB_FLASH_OUTLINE_THICKNESS, ORB_FLASH_BEEP_EVENT, } from './SelectionFeedbackConfig';
let flashOutlineTimeout = null;
let flashOutlineToken = null;
let flashOutlineSnapshot = null;
let flashAppliedState = null;
function captureOutlineState(pass) {
    const state = {
        selectedObjects: [...pass.selectedObjects],
        visibleEdgeColor: pass.visibleEdgeColor.getHex(),
        hiddenEdgeColor: pass.hiddenEdgeColor.getHex(),
        edgeThickness: pass.edgeThickness,
        edgeStrength: pass.edgeStrength,
    };
    if (typeof pass.edgeGlow === 'number') {
        state.edgeGlow = pass.edgeGlow;
    }
    if (typeof pass.pulsePeriod === 'number') {
        state.pulsePeriod = pass.pulsePeriod;
    }
    return state;
}
function applyOutlineState(pass, state) {
    pass.selectedObjects = [...state.selectedObjects];
    pass.visibleEdgeColor.setHex(state.visibleEdgeColor);
    pass.hiddenEdgeColor.setHex(state.hiddenEdgeColor);
    pass.edgeThickness = state.edgeThickness;
    pass.edgeStrength = state.edgeStrength;
    if (state.edgeGlow !== undefined) {
        pass.edgeGlow = state.edgeGlow;
    }
    if (state.pulsePeriod !== undefined) {
        pass.pulsePeriod = state.pulsePeriod;
    }
}
function outlineStatesMatch(a, b) {
    var _a, _b, _c, _d;
    if (a.selectedObjects.length !== b.selectedObjects.length) {
        return false;
    }
    for (let i = 0; i < a.selectedObjects.length; i += 1) {
        if (a.selectedObjects[i] !== b.selectedObjects[i]) {
            return false;
        }
    }
    return (a.visibleEdgeColor === b.visibleEdgeColor &&
        a.hiddenEdgeColor === b.hiddenEdgeColor &&
        a.edgeThickness === b.edgeThickness &&
        a.edgeStrength === b.edgeStrength &&
        ((_a = a.edgeGlow) !== null && _a !== void 0 ? _a : null) === ((_b = b.edgeGlow) !== null && _b !== void 0 ? _b : null) &&
        ((_c = a.pulsePeriod) !== null && _c !== void 0 ? _c : null) === ((_d = b.pulsePeriod) !== null && _d !== void 0 ? _d : null));
}
//These methods are centered around selecting objects and highlighting them with an outline effect.
/**
 * Core shared function to raycast and get the intersected object.
 */
export function getTargetObject(eventCoords) {
    const rect = global.renderer.domElement.getBoundingClientRect();
    const pointer = eventCoords
        ? new THREE.Vector2(((eventCoords.x - rect.left) / rect.width) * 2 - 1, -((eventCoords.y - rect.top) / rect.height) * 2 + 1)
        : new THREE.Vector2(0, 0);
    global.raycaster.setFromCamera(pointer, global.camera);
    const intersects = global.raycaster.intersectObjects(global.scene.children, true);
    const hit = intersects.find((entry) => !isLocalClientOrbObject(entry.object));
    return (hit === null || hit === void 0 ? void 0 : hit.object) || null;
}
function isLocalClientOrbObject(object) {
    var _a, _b;
    if (!object)
        return false;
    const localClientOrb = (_a = playerManager.local) === null || _a === void 0 ? void 0 : _a.getOrb();
    const localClientGroupUuid = (_b = localClientOrb === null || localClientOrb === void 0 ? void 0 : localClientOrb.group) === null || _b === void 0 ? void 0 : _b.uuid;
    if (!localClientGroupUuid)
        return false;
    let current = object;
    while (current) {
        if (current.uuid === localClientGroupUuid)
            return true;
        current = current.parent;
    }
    return false;
}
function isReflectiveSurface(object) {
    if (!object)
        return false;
    const anyObj = object;
    if (anyObj.isReflector || anyObj.type === 'Reflector')
        return true;
    if (typeof object.name === 'string' && object.name.toLowerCase().includes('mirror'))
        return true;
    const material = anyObj.material;
    const uniforms = material === null || material === void 0 ? void 0 : material.uniforms;
    return !!(uniforms === null || uniforms === void 0 ? void 0 : uniforms.tDiffuse);
}
function isSystemWorldMirrorBounceCandidate(primary) {
    var _a, _b, _c, _d;
    const worldName = ((_a = global.hudCurrentWorld) !== null && _a !== void 0 ? _a : '').toLowerCase();
    const sceneName = ((_d = (_c = (_b = global.sceneManager).getCurrentSceneName) === null || _c === void 0 ? void 0 : _c.call(_b)) !== null && _d !== void 0 ? _d : '').toLowerCase();
    if (worldName !== 'system' && !sceneName.includes('system'))
        return false;
    return Math.abs(primary.point.y) <= 0.25;
}
export function getTargetObjectWithReflectionBounce(eventCoords, options) {
    var _a, _b;
    const rect = global.renderer.domElement.getBoundingClientRect();
    const pointer = eventCoords
        ? new THREE.Vector2(((eventCoords.x - rect.left) / rect.width) * 2 - 1, -((eventCoords.y - rect.top) / rect.height) * 2 + 1)
        : new THREE.Vector2(0, 0);
    global.raycaster.setFromCamera(pointer, global.camera);
    const hits = global.raycaster.intersectObjects(global.scene.children, true);
    const primary = hits.find((entry) => !isLocalClientOrbObject(entry.object));
    if (!primary)
        return null;
    if (!isReflectiveSurface(primary.object) || !primary.face) {
        if (!isSystemWorldMirrorBounceCandidate(primary)) {
            return primary.object;
        }
    }
    const useSystemPlaneBounce = isSystemWorldMirrorBounceCandidate(primary);
    const worldNormal = useSystemPlaneBounce
        ? new THREE.Vector3(0, 1, 0)
        : primary.face
            ? primary.face.normal.clone().applyMatrix3(new THREE.Matrix3().getNormalMatrix(primary.object.matrixWorld)).normalize()
            : new THREE.Vector3(0, 1, 0);
    const incoming = global.raycaster.ray.direction.clone().normalize();
    const reflected = incoming.clone().reflect(worldNormal).normalize();
    const bounceOrigin = primary.point.clone().add(worldNormal.clone().multiplyScalar(0.05));
    const bounceRay = new THREE.Raycaster(bounceOrigin, reflected, 0.01, global.raycaster.far);
    const bouncedHits = bounceRay.intersectObjects(global.scene.children, true);
    const bouncedTarget = bouncedHits.find((entry) => {
        if (options === null || options === void 0 ? void 0 : options.allowLocalClientOrbOnBounce)
            return true;
        return !isLocalClientOrbObject(entry.object);
    });
    const interactionTarget = bouncedHits.find((entry) => {
        if (!(options === null || options === void 0 ? void 0 : options.allowLocalClientOrbOnBounce) && isLocalClientOrbObject(entry.object))
            return false;
        if (isReflectiveSurface(entry.object))
            return false;
        return true;
    });
    return (_b = (_a = interactionTarget === null || interactionTarget === void 0 ? void 0 : interactionTarget.object) !== null && _a !== void 0 ? _a : bouncedTarget === null || bouncedTarget === void 0 ? void 0 : bouncedTarget.object) !== null && _b !== void 0 ? _b : primary.object;
}
/**
 * Main setter function for updating selection state.
 */
export function setSelectedObject(eventCoords) {
    var _a, _b;
    const object = getTargetObjectWithReflectionBounce(eventCoords);
    if (!object) {
        console.log("Nothing selected");
        return null;
    }
    global.currentObject = object;
    console.log("Selected Object ID: " + ((_a = object.parent) === null || _a === void 0 ? void 0 : _a.uuid));
    const parent = object.parent;
    if (parent) {
        const orb = global.orbManager.getOrb(parent.uuid);
        if (orb) {
            global.hudLastSelectedOrbType = (_b = orb.orbType) !== null && _b !== void 0 ? _b : 'Unknown';
            global.hudDirty.lastSelectedOrbType = true;
        }
    }
    // not sure if this method would need to be hooked up with button type logic, probably not
    return object;
}
/**
 * Apply outline effect to selected object.
 * @param object - The THREE.Object3D to apply the outline to.
 * @param color - Optional color string (e.g., '#ff0000'); defaults to white.
 */
export function applyOutlineToObjectOld(object, color = '#ffffff', thickness = 2) {
    let outlineTarget = object;
    const parent = object.parent;
    if (parent) {
        const orb = global.orbManager.getOrb(parent.uuid);
        if (orb) {
            const outerLayer = orb.getOutermostLayer();
            if (outerLayer) {
                outlineTarget = outerLayer;
            }
        }
    }
    global.outlinePass.selectedObjects = [outlineTarget];
    global.outlinePass.visibleEdgeColor.set(color);
    global.outlinePass.hiddenEdgeColor.set(color);
    global.outlinePass.edgeThickness = thickness;
    global.outlinePass.edgeStrength = 100;
}
/**
 * Clear the current outline effect.
 */
export function clearOutline() {
    global.outlinePass.selectedObjects = [];
}
// selectionutils.ts
export function applyOutlineToObject(object, color = 0xffffff, // bright yellow default
thickness = 2) {
    let outlineTarget = object;
    const parent = object.parent;
    if (parent) {
        const orb = global.orbManager.getOrb(parent.uuid);
        if (orb) {
            const outerLayer = orb.getOutermostLayer();
            if (outerLayer)
                outlineTarget = outerLayer;
        }
    }
    global.outlinePass.selectedObjects = [outlineTarget];
    global.outlinePass.visibleEdgeColor.set(color);
    global.outlinePass.hiddenEdgeColor.set(color);
    global.outlinePass.edgeThickness = thickness;
    global.outlinePass.edgeStrength = 100; // << was 100
    global.outlinePass.edgeGlow = 0; // optional
    global.outlinePass.pulsePeriod = 0; // optional
}
export function flashOutlineForObject(object, options) {
    if (!object)
        return;
    const outlinePass = global.outlinePass;
    if (!outlinePass)
        return;
    if (flashOutlineTimeout !== null) {
        window.clearTimeout(flashOutlineTimeout);
        flashOutlineTimeout = null;
        flashOutlineToken = null;
        flashOutlineSnapshot = null;
        flashAppliedState = null;
    }
    const snapshot = captureOutlineState(outlinePass);
    flashOutlineSnapshot = snapshot;
    const token = Symbol('flash-outline');
    flashOutlineToken = token;
    const { color = ORB_FLASH_OUTLINE_COLOR, thickness = ORB_FLASH_OUTLINE_THICKNESS, durationMs = ORB_FLASH_FEEDBACK_DURATION_MS, } = options !== null && options !== void 0 ? options : {};
    applyOutlineToObject(object, color, thickness);
    flashAppliedState = captureOutlineState(outlinePass);
    if (global.audioBus) {
        global.audioBus.trigger(ORB_FLASH_BEEP_EVENT);
    }
    flashOutlineTimeout = window.setTimeout(() => {
        if (flashOutlineToken !== token) {
            return;
        }
        const currentState = captureOutlineState(outlinePass);
        if (!flashAppliedState || !outlineStatesMatch(currentState, flashAppliedState)) {
            flashOutlineTimeout = null;
            flashOutlineToken = null;
            flashOutlineSnapshot = null;
            flashAppliedState = null;
            return;
        }
        if (flashOutlineSnapshot) {
            applyOutlineState(outlinePass, flashOutlineSnapshot);
        }
        else {
            clearOutline();
        }
        flashOutlineTimeout = null;
        flashOutlineToken = null;
        flashOutlineSnapshot = null;
        flashAppliedState = null;
    }, durationMs);
}
// rough utility: get a world point from the pointer (fallback: ray far point)
export function worldPointFromPointer(x, y) {
    const rect = global.renderer.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(((x - rect.left) / rect.width) * 2 - 1, -((y - rect.top) / rect.height) * 2 + 1);
    global.raycaster.setFromCamera(ndc, global.camera);
    const hits = global.raycaster.intersectObjects(global.scene.children, true);
    if (hits.length)
        return hits[0].point.clone();
    // fallback: a point along the ray
    const ray = global.raycaster.ray;
    return ray.origin.clone().add(ray.direction.clone().multiplyScalar(1000));
}
